///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2021 - 2021
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { times } from "lodash";

import getPricingGridColumns from "./getPricingGridColumns";
import handlePricingCellChange from "./handlePricingCellChange";
import { getRange } from "./tableSelection";
import { cellUnit } from "./types";

const isSingleValuePastingToMultipleCell = ({ startCell, endCell, text }) => {
  const isValid =
    startCell &&
    endCell &&
    text.indexOf("\n") === -1 &&
    text.indexOf("\t") === -1 &&
    startCell.colIndex === endCell.colIndex;

  const textArray = [];
  let range;
  if (isValid) {
    range = getRange(startCell, endCell);
    times(range.endRow - range.startRow + 1, () => textArray.push(text));
  }
  return { isValid, textArray, range };
};

const isMultipleCellsPastingIntoMultipleCells = ({
  startCell,
  endCell,
  text
}) => {
  const textArray = text.split("\n");
  const range = startCell && endCell && getRange(startCell, endCell);

  const isValid =
    startCell &&
    endCell &&
    textArray.length > 1 &&
    text.indexOf("\t") === -1 &&
    startCell.colIndex === endCell.colIndex &&
    range.endRow - range.startRow + 1 >= textArray.length;
  return { isValid, textArray, range };
};

const isMultiValuesPastedFromSingleCell = ({
  endCell,
  text,
  rowIndex,
  rowCount
}) => {
  const textArray = text.split("\n");

  const isValid = !endCell && textArray.length > 1 && text.indexOf("\t") === -1;
  const range = {
    startRow: rowIndex,
    endRow:
      rowCount < textArray.length + rowIndex
        ? rowCount - 1
        : textArray.length + rowIndex - 1
  };
  return { isValid, textArray, range };
};

export const isSpecialPaste = (text, rowIndex, rowCount) => {
  const { startCell, endCell } = window;

  const singleValueToMultiCell = isSingleValuePastingToMultipleCell({
    startCell,
    endCell,
    text
  });

  const multiValuesPastedInMultiCells =
    !singleValueToMultiCell.isValid &&
    isMultipleCellsPastingIntoMultipleCells({ startCell, endCell, text });

  const multiValuesPastedFromSingleCell =
    !singleValueToMultiCell.isValid &&
    !multiValuesPastedInMultiCells.isValid &&
    isMultiValuesPastedFromSingleCell({
      endCell,
      text,
      rowIndex,
      rowCount
    });

  const isValid =
    singleValueToMultiCell.isValid ||
    multiValuesPastedInMultiCells.isValid ||
    multiValuesPastedFromSingleCell.isValid;

  let textArray = [];
  let range;

  if (singleValueToMultiCell.isValid) {
    textArray = singleValueToMultiCell.textArray;
    range = singleValueToMultiCell.range;
  } else if (multiValuesPastedInMultiCells.isValid) {
    textArray = multiValuesPastedInMultiCells.textArray;
    range = multiValuesPastedInMultiCells.range;
  } else if (multiValuesPastedFromSingleCell.isValid) {
    textArray = multiValuesPastedFromSingleCell.textArray;
    range = multiValuesPastedFromSingleCell.range;
  }
  return { isValid, textArray, range };
};

export const handleSpecialPastingInGrid = (
  state,
  { payload: { textArray, range, role, field } }
) => {
  const { startRow, endRow } = range;
  const { parse } = getPricingGridColumns(role, state.division, null, state.isCoyoteBid).find(
    c => c.field === field
  );
  const { offset } = state;
  
  const matchedLanes = state.lanes.filter(lane => lane.metaData.matched);
  
  if (
    matchedLanes.some(
      (l, index) => startRow <= index && endRow >= index && l.metaData.disabled
    )
  ) {
    return;
  }
  
  for (
    let i = startRow, cellCounter = 0;
    i <= endRow && cellCounter < textArray.length;
    i++, cellCounter++
  ) {
    const pasteTargetIndex = offset + i;
    
    if (pasteTargetIndex >= matchedLanes.length) {
      break;
    }
    
    const {
      metaData: { defaultIndex: index }
    } = matchedLanes[pasteTargetIndex];

    const text = parse
      ? textArray[cellCounter]
          .replace(cellUnit.percent, "")
          .replace(cellUnit.dollar, "")
      : textArray[cellCounter];

    const parseDetails = parse && parse(text);

    if (!parse || parseDetails.isValid) {
      handlePricingCellChange(state, {
        payload: {
          index,
          field,
          value: parse ? parseDetails.value : text,
          role
        }
      });
    }
  }
};

export default handleSpecialPastingInGrid;
